import { ServiceProvider, StorageItem, UPLOADCAM_FOLDER_MIME } from '@uploadcam/common';
import { GetCredentialInteractor } from '../../../auth/domain/interactors/get-credential.interactor';
import { RefreshCredentialInteractor } from '../../../auth/domain/interactors/refresh-credential.interactor';
import { GoogleApiService } from '../../../common/google/google-api.service';
import { MockItem } from '../../data/common/mock.item';
import { StorageDialogService } from '../../storage-dialog.service';
import { StorageProvider } from '../../storage.provider';
import {
  DialogService,
  MessageType,
} from '../../../../app/features/shared/services/dialog.service';

export class ShowFolderPickerInteractor {
  constructor(
    private readonly dialog: DialogService,
    private readonly getCredential: GetCredentialInteractor,
    private readonly googleApiService: GoogleApiService,
    private readonly refreshCredential: RefreshCredentialInteractor,
    private readonly storageDialog: StorageDialogService,
    private readonly storageProvider: StorageProvider,
  ) {}

  public async execute(
    title: string,
    foldersBlacklist: string[],
    disableBlacklistBrowse: boolean,
  ): Promise<StorageItem | undefined> {
    const credential = this.getCredential.execute();
    let selectedItem: StorageItem | undefined;

    if (
      // TODO: Replace with a `features.ts` `can*` check
      credential.provider === ServiceProvider.Google &&
      !credential.scopes.includes('https://www.googleapis.com/auth/drive')
    ) {
      selectedItem = await this.showGoogleFolderPicker(credential, title);
    } else {
      const manager = await this.storageProvider.provideManager();
      const root = await manager.getRootFolder();

      selectedItem = await this.storageDialog.openSelectFolderDialog({
        title,
        config: {
          root,
          canBrowse: (item) =>
            disableBlacklistBrowse && foldersBlacklist.includes(item.id) ? false : true,
          isDisabled: (currentFolder, item) => {
            if (disableBlacklistBrowse && foldersBlacklist.includes(currentFolder.id)) {
              return {
                state: true,
                reason: `Picking a subfolder of the team root or an existing folder is not allowed`,
              };
            }

            return {
              state: currentFolder.id !== root.id && foldersBlacklist.includes(item.id),
              reason: 'This folder is already a Team Folder',
            };
          },
          listFolder: (item, onlyFolders) => manager.listFolder(item, onlyFolders),
          setSelected: (selected) => {
            const isNotVirtual = !selected?.isVirtual;
            const isNotBlacklisted = selected ? !foldersBlacklist.includes(selected.id) : true;

            if (isNotVirtual && isNotBlacklisted) {
              return selected;
            } else {
              return undefined;
            }
          },
          showCreateFolder: (folder) => folder.allowsSubfolderCreation,
        },
      });
    }

    if (selectedItem && foldersBlacklist.includes(selectedItem.id)) {
      this.dialog.snack(
        `The selected folder is not allowed: it's the Team root folder or it's already used in a Folder.`,
        MessageType.Error,
      );

      return undefined;
    }

    return selectedItem;
  }

  private async showGoogleFolderPicker(
    credential: any,
    title: string,
  ): Promise<StorageItem | undefined> {
    if (credential.isExpired) {
      credential = await this.refreshCredential.execute(credential);
    }

    const folder = await this.googleApiService.showFolderPicker(credential.accessToken, title);

    if (folder) {
      return new MockItem(
        folder['id'],
        ServiceProvider.Google,
        undefined, // TODO: Get `driveId` from file?
        undefined,
        false,
        false,
        new Date(folder['lastEditedUtc']),
        {},
        UPLOADCAM_FOLDER_MIME,
        folder['name'],
        0,
      );
    }

    return undefined;
  }
}
